import * as Sentry from '@sentry/vue';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { CaptchaError } from 'types/error-tracking';
import { getWafToken } from '@utils/waf';
import { logError } from '@utils/error-tracking';
import { useAppStore } from '@stores/app';
import WafCaptchaModal from '@components/modals/waf-captcha-modal.vue';

const CHALLENGE_MAX_RETRIES = 2;
let isChallengeFetching = false;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let challengeSubscribers: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = [];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function addChallengeSubscriber(resolve: (token: string) => void, reject: (err: any) => void): void {
	challengeSubscribers.push({ resolve, reject });
}

function onChallengeTokenFetched(token: string): void {
	challengeSubscribers.forEach(({ resolve }) => resolve(token));
	challengeSubscribers = [];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function onChallengeError(err: any): void {
	Sentry.addBreadcrumb({
		category: 'waf',
		message: 'WAF challenge failed, rejecting all subscribers',
		level: 'error',
		data: {
			subscriberCount: challengeSubscribers.length,
			error: err.message || String(err)
		}
	});
	challengeSubscribers.forEach(({ reject }) => reject(err));
	challengeSubscribers = [];
}

/**
 * When we receive status code 202 that has a 'x-amzn-waf-action' header of challenge,
 * this retries the request with a new 'x-aws-waf-token' header
 */
export function WafChallengeResponse(response: AxiosResponse, instance: AxiosInstance): Promise<unknown> {
	const { config } = response;
	const challengeStatusCode = 202;
	const hasChallengeStatus = response?.status === challengeStatusCode;
	const hasChallengeHeader = response?.headers['x-amzn-waf-action'] === 'challenge';

	if (!hasChallengeStatus || !hasChallengeHeader) {
		return Promise.resolve(response);
	}

	if (config.wafChallengeRetryCount && config.wafChallengeRetryCount >= CHALLENGE_MAX_RETRIES) {
		return Promise.reject(new Error('WAF retry failed (may be invalid token)'));
	}

	if (!isChallengeFetching) {
		isChallengeFetching = true;

		getWafToken()
			.then((wafToken) => {
				if (!wafToken) {
					Sentry.addBreadcrumb({
						category: 'waf',
						message: 'Resolved WAF challenge is invalid',
						level: 'info',
						data: {
							typeOf: typeof wafToken
						}
					});
				}

				Sentry.addBreadcrumb({
					category: 'waf',
					message: 'WAF challenge retry started',
					level: 'info',
					data: { retryUsingSameTok: wafToken === config.headers?.['x-aws-waf-token'] }
				});
				onChallengeTokenFetched(wafToken ?? '');
			})
			.catch((err) => {
				logError(err, 'error', {
					key: 'waf-challenge-error',
					data: {
						message: err.message || 'Unknown WAF challenge error',
						requestUrl: config.url,
						requestMethod: config.method
					}
				});
				onChallengeError(err);
			})
			.finally(() => {
				isChallengeFetching = false;
			});
	}

	return new Promise((resolve, reject) => {
		addChallengeSubscriber((token: string) => {
			config.headers = { ...config.headers, ...{ 'x-aws-waf-token': token } };
			config.isRetry = true;
			config.wafChallengeRetryCount = config.wafChallengeRetryCount ? config.wafChallengeRetryCount + 1 : 1;
			instance(config).then(resolve).catch(reject);
		}, reject);
	});
}

let isCaptchaFetching = false;
let captchaSubscribers: Array<(args: string) => void> = [];

function addCaptchaSubscriber(cb: (args: string) => void): void {
	captchaSubscribers.push(cb);
}

function onCaptchaTokenFetched(token: string): void {
	captchaSubscribers.forEach((callback) => callback(token));
	captchaSubscribers = [];
}

function onCaptchaError(err: AxiosError): void {
	captchaSubscribers.forEach(() => Promise.reject(err));
	captchaSubscribers = [];
}

/**
 * When we receive status code 405 that has a 'x-amzn-waf-action' header of captcha,
 * this renders the WAF captcha modal and handles success/failure.
 */
export function WafCaptchaResponse(err: AxiosError, instance: AxiosInstance): Promise<unknown> {
	const { config } = err;
	const wafStatusCode = 405;
	const hasWafErrorStatus = err?.response?.status === wafStatusCode;
	const wafActionHeader = err.response?.headers['x-amzn-waf-action'];
	const wafActionIsCaptcha = wafActionHeader === 'captcha';
	const showWafCaptcha = hasWafErrorStatus && wafActionIsCaptcha;

	if (hasWafErrorStatus && wafActionHeader === undefined) {
		logError('405 without x-amzn-waf-action header exposed', 'error');
	}

	if (!showWafCaptcha) {
		return Promise.reject(err);
	}

	if (!isCaptchaFetching) {
		const appStore = useAppStore();
		isCaptchaFetching = true;
		appStore.updateCurrentModal({
			modal: WafCaptchaModal,
			props: {
				resolve: async (captchaToken: string) => {
					isCaptchaFetching = false;
					onCaptchaTokenFetched(captchaToken);
				},
				reject: (captchaError: CaptchaError) => {
					isCaptchaFetching = false;
					logError(captchaError, 'error', { key: 'waf-captcha-error', data: { ...captchaError } });
					onCaptchaError(err);
				}
			}
		});
	}

	return new Promise((resolve, reject) => {
		addCaptchaSubscriber((token: string) => {
			config.headers = { ...config.headers, ...{ 'x-aws-waf-token': token } };
			config.isRetry = true;
			instance(config).then(resolve).catch(reject);
		});
	});
}

export function attachWafChallengeCaptchaInterceptor(instance: AxiosInstance): number {
	return instance.interceptors.response.use(
		(response: AxiosResponse) => WafChallengeResponse(response, instance),
		(err: AxiosError) => WafCaptchaResponse(err, instance)
	);
}
